"use client";

import { landingCopy } from "./copy";
import {
  TrendingUp,
  FileText,
  RefreshCw,
  BarChart3,
  MessageSquare,
  Workflow,
  Users,
  Activity,
  type LucideIcon,
} from "lucide-react";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { LandingSectionBadge } from "./LandingSectionBadge";
import { cn } from "@/lib/utils";

const iconMap: Record<string, LucideIcon> = {
  TrendingUp,
  FileText,
  RefreshCw,
  BarChart3,
  MessageSquare,
  Workflow,
  Users,
  Activity,
};

const accentStyles = [
  { gradient: "from-blue-500 to-cyan-500", glow: "from-blue-500/20 to-cyan-500/10", ring: "ring-blue-500/15" },
  { gradient: "from-cyan-500 to-blue-600", glow: "from-cyan-500/20 to-blue-600/10", ring: "ring-cyan-500/15" },
  { gradient: "from-blue-600 to-sky-400", glow: "from-blue-600/20 to-sky-400/10", ring: "ring-blue-600/15" },
  { gradient: "from-sky-500 to-cyan-600", glow: "from-sky-500/20 to-cyan-600/10", ring: "ring-sky-500/15" },
  { gradient: "from-blue-500 to-indigo-500", glow: "from-blue-500/20 to-indigo-500/10", ring: "ring-blue-500/15" },
  { gradient: "from-cyan-600 to-blue-500", glow: "from-cyan-600/20 to-blue-500/10", ring: "ring-cyan-600/15" },
  { gradient: "from-indigo-500 to-cyan-500", glow: "from-indigo-500/20 to-cyan-500/10", ring: "ring-indigo-500/15" },
  { gradient: "from-blue-500 to-teal-500", glow: "from-blue-500/20 to-teal-500/10", ring: "ring-blue-500/15" },
];

export default function Features() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08, delayChildren: 0.06 },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 36 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] as const },
    },
  };

  return (
    <section
      id="features"
      className="relative overflow-hidden border-y border-border/40 bg-gradient-to-b from-muted/30 via-background to-muted/20 py-24 lg:py-32"
    >
      <div className="pointer-events-none absolute inset-0" aria-hidden>
        <div
          className="absolute inset-0 opacity-[0.4] dark:opacity-[0.15]"
          style={{
            backgroundImage: `radial-gradient(oklch(0.55 0.18 245 / 0.05) 1px, transparent 1px)`,
            backgroundSize: "32px 32px",
          }}
        />
        <div className="absolute -left-20 top-24 h-[420px] w-[420px] rounded-full bg-primary/[0.09] blur-3xl" />
        <div className="absolute -right-24 bottom-32 h-[360px] w-[360px] rounded-full bg-cyan-500/[0.07] blur-3xl dark:bg-cyan-400/[0.05]" />
        <div className="absolute left-1/2 top-1/2 h-[min(90vw,640px)] w-[min(90vw,640px)] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-tr from-primary/[0.04] via-transparent to-cyan-500/[0.05]" />
      </div>

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="mx-auto mb-16 max-w-3xl text-center lg:mb-20"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <motion.div
            className="flex justify-center"
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 380, damping: 24 }}
          >
            <LandingSectionBadge className="mb-5">Platform capabilities</LandingSectionBadge>
          </motion.div>

          <div className="relative mx-auto inline-block max-w-full">
            <h2 className="mb-5 text-balance text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl lg:leading-[1.15]">
              <span className="bg-gradient-to-r from-primary via-blue-600 to-cyan-600 bg-clip-text text-transparent">
                Everything you need for debt recovery
              </span>
            </h2>
            <div
              className="mx-auto mb-6 h-1 w-16 rounded-full bg-gradient-to-r from-primary via-blue-500 to-cyan-500 opacity-80"
              aria-hidden
            />
            <p className="text-pretty text-lg leading-relaxed text-muted-foreground sm:text-xl">
              Powerful features designed to automate your debt recovery process and maximize collection
              efficiency
            </p>
          </div>
        </motion.div>

        <motion.div
          ref={ref}
          className="mx-auto grid max-w-6xl grid-cols-1 gap-5 sm:gap-6 md:grid-cols-2 lg:grid-cols-6 lg:gap-6"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {landingCopy.features.map((feature, index) => {
            const Icon = iconMap[feature.icon] || TrendingUp;
            const accent = accentStyles[index % accentStyles.length];
            const featured = index < 2;

            return (
              <motion.div
                key={feature.name}
                variants={cardVariants}
                className={cn(
                  index < 2 ? "md:col-span-2 lg:col-span-3" : "lg:col-span-2",
                  featured && "lg:min-h-[280px]"
                )}
              >
                <article
                  className={cn(
                    "group relative flex h-full flex-col overflow-hidden rounded-3xl border border-border/60 bg-gradient-to-b from-card/95 via-card/90 to-muted/20 p-6 shadow-sm transition-all duration-300",
                    "hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-[0_24px_60px_-28px_oklch(0.55_0.18_245_/_0.2)]",
                    featured && "lg:p-8"
                  )}
                >
                  <div
                    className={cn(
                      "pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100",
                      `bg-gradient-to-br ${accent.glow}`
                    )}
                    aria-hidden
                  />
                  <div
                    className={cn(
                      "pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/35 to-transparent opacity-60"
                    )}
                    aria-hidden
                  />

                  <div className={cn("relative flex flex-1 flex-col", featured && "lg:flex-row lg:gap-8")}>
                    <div
                      className={cn(
                        "relative mb-5 flex shrink-0 items-start",
                        featured ? "lg:mb-0" : "mb-5"
                      )}
                    >
                      <div
                        className={cn(
                          "flex items-center justify-center rounded-2xl bg-gradient-to-br shadow-lg ring-1 ring-inset transition-transform duration-300 group-hover:scale-[1.04]",
                          accent.gradient,
                          accent.ring,
                          featured ? "h-14 w-14 lg:h-16 lg:w-16" : "h-12 w-12"
                        )}
                      >
                        <Icon
                          className={cn("text-white", featured ? "h-7 w-7 lg:h-8 lg:w-8" : "h-6 w-6")}
                          strokeWidth={1.75}
                        />
                      </div>
                    </div>

                    <div className="relative flex min-w-0 flex-1 flex-col">
                      <div className="mb-1 flex items-center gap-2">
                        <span className="font-mono text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-muted-foreground/80">
                          {String(index + 1).padStart(2, "0")}
                        </span>
                      </div>
                      <h3
                        className={cn(
                          "mb-3 font-semibold tracking-tight text-foreground",
                          featured ? "text-xl lg:text-2xl" : "text-lg"
                        )}
                      >
                        {feature.name}
                      </h3>
                      <p
                        className={cn(
                          "leading-relaxed text-muted-foreground",
                          featured ? "text-base lg:text-[1.05rem] lg:leading-relaxed" : "text-sm lg:text-[0.95rem]"
                        )}
                      >
                        {feature.benefit}
                      </p>
                    </div>
                  </div>
                </article>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
